/**
 * Browser page translation (Chrome's built-in Google Translate, DeepL & co.) and a
 * few extensions rewrite the DOM underneath React: Translate replaces every text
 * node with `<font><font>translated</font></font>` and detaches the original. React
 * 18 keeps pointing at the detached node, so the next time it removes that text (a
 * conditional "+" disappearing) or inserts before it, the browser throws
 * NotFoundError and the whole page falls into the error boundary. Known React
 * limitation (facebook/react#11538); the accepted mitigation is to make the two
 * DOM calls tolerant: when the node React refers to is no longer where React left
 * it, skip the operation instead of throwing. The affected text stays as the
 * translator rendered it until the element re-mounts — stale, but not a crash.
 *
 * Must run before React renders. Warns once per operation so a genuine
 * reconciliation bug would still show up in the console.
 */
export function installDomMutationGuard(): void {
  if (typeof Node !== 'function') return;
  const proto = Node.prototype;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- captured to be invoked with .call below
  const { removeChild, insertBefore } = proto;
  const warned = new Set<string>();
  const warn = (op: string, node: Node, parent: Node): void => {
    if (warned.has(op)) return;
    warned.add(op);
    console.warn(
      `[dom-guard] ${op} skipped: node is no longer a child of its React parent (page translated?)`,
      node,
      parent,
    );
  };

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warn('removeChild', child, this);
      return child;
    }
    return removeChild.call(this, child) as T;
  };
  proto.insertBefore = function <T extends Node>(this: Node, node: T, reference: Node | null): T {
    if (reference && reference.parentNode !== this) {
      warn('insertBefore', reference, this);
      return node;
    }
    return insertBefore.call(this, node, reference) as T;
  };
}
